import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useNavigate, useSearchParams } from 'react-router';
import { useTranslation } from 'react-i18next';

import { Button } from '@/shared/ui/components/button';
import { Input } from '@/shared/ui/components/input';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/shared/ui/components/form';
import { changePassword, ChangePasswordError, type ChangePasswordInput } from './api';
import { AUTH_ME_QUERY_KEY, useAuth } from './AuthContext';

const MIN_PASSWORD_LENGTH = 12;

const schema = z
  .object({
    currentPassword: z.string().min(1),
    newPassword: z.string().min(MIN_PASSWORD_LENGTH),
    confirmPassword: z.string().min(1),
  })
  .refine((values) => values.newPassword === values.confirmPassword, {
    path: ['confirmPassword'],
    message: 'mismatch',
  });

type FormValues = z.infer<typeof schema>;

export function ChangePasswordPage() {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { mustChangePassword } = useAuth();
  const [params] = useSearchParams();
  const from = params.get('from') || '/';

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { currentPassword: '', newPassword: '', confirmPassword: '' },
    mode: 'onSubmit',
  });

  const mutation = useMutation({
    mutationFn: (input: ChangePasswordInput) => changePassword(input),
    onSuccess: async () => {
      await queryClient.refetchQueries({ queryKey: AUTH_ME_QUERY_KEY });
      // A forced change lands on the dashboard; a voluntary change returns whence it came.
      navigate(mustChangePassword ? '/' : from, { replace: true });
    },
  });

  const submitError =
    mutation.error instanceof ChangePasswordError
      ? mutation.error.code === 'invalid_current_password'
        ? t('auth.changePassword.errors.invalidCurrent')
        : t('auth.changePassword.errors.generic')
      : mutation.error
        ? t('auth.changePassword.errors.generic')
        : null;

  return (
    <section className="mx-auto grid max-w-sm gap-6 p-6">
      <div className="grid gap-1">
        <h1 className="text-2xl font-semibold">{t('auth.changePassword.title')}</h1>
        {mustChangePassword && (
          <p className="text-muted-foreground text-sm">{t('auth.changePassword.forced')}</p>
        )}
      </div>
      <Form {...form}>
        <form
          className="grid gap-4"
          onSubmit={form.handleSubmit((values) =>
            mutation.mutate({
              currentPassword: values.currentPassword,
              newPassword: values.newPassword,
            }),
          )}
          noValidate
        >
          <FormField
            control={form.control}
            name="currentPassword"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('auth.changePassword.current')}</FormLabel>
                <FormControl>
                  <Input type="password" autoComplete="current-password" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="newPassword"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('auth.changePassword.new')}</FormLabel>
                <FormControl>
                  <Input type="password" autoComplete="new-password" {...field} />
                </FormControl>
                <FormMessage>
                  {form.formState.errors.newPassword
                    ? t('auth.changePassword.errors.tooShort')
                    : null}
                </FormMessage>
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="confirmPassword"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('auth.changePassword.confirm')}</FormLabel>
                <FormControl>
                  <Input type="password" autoComplete="new-password" {...field} />
                </FormControl>
                <FormMessage>
                  {form.formState.errors.confirmPassword
                    ? t('auth.changePassword.errors.mismatch')
                    : null}
                </FormMessage>
              </FormItem>
            )}
          />
          {submitError && (
            <p role="alert" className="text-destructive text-sm">
              {submitError}
            </p>
          )}
          <Button type="submit" disabled={mutation.isPending}>
            {mutation.isPending
              ? t('auth.changePassword.submitting')
              : t('auth.changePassword.submit')}
          </Button>
        </form>
      </Form>
    </section>
  );
}
